/**
 * All stores that are shared throughout the app should be created here. Currently this is every store in the app,
 * but can be broken out into smaller files as smaller stand-alone React apps are extracted.
 */
import { legacyCreateAiAssistantStore } from '@/aiAssistant/aiAssistant.store';
import { legacyCreateAnnotationStore } from '@/annotation/annotation.store';
import { AssetGroupStore } from '@/assetGroupEditor/assetGroup.store';
import { flux } from '@/core/flux.module';
import { legacyCreateExploreFolderModalStore } from '@/explorer/exploreFolderModal.store';
import { legacyCreateExploreWorkbookModalStore } from '@/explorer/exploreWorkbookModal.store';
import { legacyCreateHomeScreenStore } from '@/homescreen/homescreen.store';
import { ItemFinderStore } from '@/itemFinder/itemFinder.store';
import { legacyCreateLicenseManagementStore } from '@/licenseManagement/licenseManagement.store';
import { legacyCreateLayoutStore } from '@/main/layout.store';
import { PluginStore } from '@/plugin/plugin.store';
import { legacyCreatePdfExportStore } from '@/reportEditor/pdfExport.store';
import { ReportStore } from '@/reportEditor/report.store';
import { ReportContentStore } from '@/reportEditor/reportContent.store';
import { ScatterPlotStore } from '@/scatterPlot/scatterPlot.store';
import { legacyCreateSearchStore } from '@/search/search.store';
import { StateSynchronizer } from '@/services/stateSynchronizer.service';
import { legacyCreateStoredTableStore } from '@/storedTableEditor/storedTable.store';
import { legacyCreateSummaryReportStore } from '@/summaryReport/summaryReport.store';
import { legacyCreateScorecardStore } from '@/tableBuilder/scorecard.store';
import { TableBuilderStore } from '@/tableBuilder/tableBuilder.store';
import { AssetBuilderStore } from '@/tableDefinitionEditor/assetBuilder.store';
import { legacyCreateEditTemplatePreviewStore } from '@/tableDefinitionEditor/editTemplatePreview.store';
import { ItemTableDefinitionStore } from '@/tableDefinitionEditor/itemTableDefinition.store';
import { ItemTableEditorStore } from '@/tableDefinitionEditor/itemTableEditorStore';
import { ScalingTableDefinitionStore } from '@/tableDefinitionEditor/scalingTableDefinition.store';
import { ScalingTableEditorStore } from '@/tableDefinitionEditor/scalingTableEditorStore';
import { legacyCreateScalingTemplateStore } from '@/tableDefinitionEditor/scalingTemplate.store';
import { InvestigateStore } from '@/toolSelection/investigate.store';
import { ClusteringStore } from '@/tools/clustering/clustering.store';
import { legacyCreateClusteringV2Store } from '@/tools/clusteringV2/clustering.store';
import { legacyCreateCompositeSearchStore } from '@/tools/compositeSearch/compositeSearch.store';
import { legacyCreateConditionWithPropertiesStore } from '@/tools/conditionWithProperties/conditionWithProperties.store';
import { legacyCreateDigitalFilterStore } from '@/tools/digitalFilter/digitalFilter.store';
import { legacyCreateDisplayToolStore } from '@/tools/display/displayTool.store';
import { legacyCreateExportExcelPanelStore } from '@/tools/exportExcel/exportExcelPanel.store';
import { legacyCreateExportODataPanelStore } from '@/tools/exportOData/exportODataPanel.store';
import { FormulaToolStore } from '@/tools/formula/formulaTool.store';
import { legacyCreateFftStore } from '@/tools/frequencyAnalysis/fft.store';
import { legacyCreateAggregationBinStore } from '@/tools/histogram/aggregationBin.store';
import { legacyCreateImportDatafileStore } from '@/tools/importDatafile/importDatafile.store';
import { IsolationForestStore } from '@/tools/isolationForest/isolationForest.store';
import { legacyCreateIsolationForestV2Store } from '@/tools/isolationForestV2/isolationForest.store';
import { legacyCreateItemPropertiesStore } from '@/tools/itemProperties/propertiesPanel.store';
import { legacyCreateCapsuleGroupStore } from '@/tools/manualCondition/capsuleGroup.store';
import { legacyCreateManualConditionStore } from '@/tools/manualCondition/manualCondition.store';
import { ManualSignalStore } from '@/tools/manualSignal/manualSignal.store';
import { legacyCreateToolTemplateStore } from '@/tools/models/toolTemplate.store';
import { legacyCreateOnnxToolStore } from '@/tools/onnx/onnx.store';
import { legacyCreateOperationalizeStatisticsSummaryStore } from '@/tools/operationalizeStatisticsSummary/operationalizeStatisticsSummary.store';
import { PartialLeastSquaresStore } from '@/tools/partialLeastSquares/partialLeastSquares.store';
import { legacyCreatePartialLeastSquaresV2Store } from '@/tools/partialLeastSquaresV2/partialLeastSquares.store';
import { legacyCreatePeriodicConditionStore } from '@/tools/periodicCondition/periodicCondition.store';
import { legacyCreatePredictionPanelStore } from '@/tools/prediction/predictionPanel.store';
import { PrincipalComponentAnalysisStore } from '@/tools/principalComponentAnalysis/principalComponentAnalysis.store';
import { legacyCreatePrincipalComponentAnalysisV2Store } from '@/tools/principalComponentAnalysisV2/principalComponentAnalysis.store';
import { legacyCreateProfileSearchStore } from '@/tools/profileSearch/profileSearch.store';
import { legacyCreateReferencePanelStore } from '@/tools/referencePanel/referencePanel.store';
import { legacyCreateScatterConditionStore } from '@/tools/scatterPlotSelection/scatterCondition.store';
import { SelfOrganizingMapsStore } from '@/tools/selfOrganizingMaps/selfOrganizingMaps.store';
import { legacyCreateSelfOrganizingMapsV2Store } from '@/tools/selfOrganizingMapsV2/selfOrganizingMaps.store';
import { legacyCreateSignalForecastStore } from '@/tools/signalForecast/signalForecast.store';
import { legacyCreateSignalFromConditionStore } from '@/tools/signalFromCondition/signalFromCondition.store';
import { legacyCreateSignalSmoothingStore } from '@/tools/signalSmoothing/signalSmoothing.store';
import { legacyCreateStatisticsSummaryStore } from '@/tools/statisticsSummary/statisticsSummary.store';
import { legacyCreateThresholdMetricStore } from '@/tools/thresholdMetric/thresholdMetric.store';
import { legacyCreateViewStatisticSummariesStore } from '@/tools/viewStatisticSummaries/viewStatisticSummaries.store';
import { TreemapStore } from '@/treemap/treemap.store';
import { legacyCreateCapsuleRuntimeStore } from '@/trendData/capsuleRuntime.store';
import { legacyCreateConditionRuntimeStore } from '@/trendData/conditionRuntime.store';
import { legacyCreateCursorStore } from '@/trendData/cursor.store';
import { DurationStore } from '@/trendData/duration.store';
import { legacyCreateItemRuntimeStore } from '@/trendData/itemRuntime.store';
import { legacyCreateScalarRuntimeStore } from '@/trendData/scalarRuntime.store';
import { legacyCreateSeriesRuntimeStore } from '@/trendData/seriesRuntime.store';
import { legacyCreateTableRuntimeStore } from '@/trendData/tableRuntime.store';
import { TrendStore } from '@/trendData/trend.store';
import { TrendCapsuleStore } from '@/trendData/trendCapsule.store';
import { TrendConditionStore } from '@/trendData/trendCondition.store';
import { TrendMetricStore } from '@/trendData/trendMetric.store';
import { TrendScalarStore } from '@/trendData/trendScalar.store';
import { legacyCreateTrendScalingColumnStore } from '@/trendData/trendScalingColumn.store';
import { TrendSeriesStore } from '@/trendData/trendSeries.store';
import { TrendTableStore } from '@/trendData/trendTable.store';
import { legacyCreateValueSearchStore } from '@/trendData/valueSearch.store';
import { VantageContextStore } from '@/vantage/vantageContext.store';
import { VantageTableStore } from '@/vantage/vantageTable.store';
import { WorkbenchStore } from '@/workbench/workbench.store';
import { WorkbookStore } from '@/workbook/workbook.store';
import { WorksheetStore } from '@/worksheet/worksheet.store';
import { legacyCreateWorkstepsStore } from '@/worksteps/worksteps.store';
import { isDebugStoresDevtoolsEnabled, sendDevtoolsState } from '@seeqdev/utils';

export const sqStateSynchronizer = new StateSynchronizer(flux);
flux.setDispatchCallback((handler, payload, option) => {
  if (isDebugStoresDevtoolsEnabled()) {
    sendDevtoolsState({
      type: handler,
      payload: payload as Record<string, any> | undefined,
    });
  }

  sqStateSynchronizer.push(option);
});

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqAnnotationStore = legacyCreateAnnotationStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqWorkstepsStore = legacyCreateWorkstepsStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqLicenseManagementStore = legacyCreateLicenseManagementStore();
/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqOperationalizeStatisticsSummaryStore = legacyCreateOperationalizeStatisticsSummaryStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqPdfExportStore = legacyCreatePdfExportStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqSearchStore = legacyCreateSearchStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqModalSearchStore = legacyCreateSearchStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqDisplayToolStore = legacyCreateDisplayToolStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqExploreFolderModalStore = legacyCreateExploreFolderModalStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqDigitalFilterStore = legacyCreateDigitalFilterStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqFftStore = legacyCreateFftStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqScorecardStore = legacyCreateScorecardStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqAiAssistantStore = legacyCreateAiAssistantStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqExploreWorkbookModalStore = legacyCreateExploreWorkbookModalStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqOnnxStore = legacyCreateOnnxToolStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqToolTemplateStore = legacyCreateToolTemplateStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqCursorStore = legacyCreateCursorStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqClusteringStoreV2 = legacyCreateClusteringV2Store();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqSelfOrganizingMapsStoreV2 = legacyCreateSelfOrganizingMapsV2Store();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqIsolationForestStoreV2 = legacyCreateIsolationForestV2Store();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqPartialLeastSquaresStoreV2 = legacyCreatePartialLeastSquaresV2Store();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqPrincipalComponentAnalysisStoreV2 = legacyCreatePrincipalComponentAnalysisV2Store();
/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqThresholdMetricStore = legacyCreateThresholdMetricStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqScalingTemplateStore = legacyCreateScalingTemplateStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqStatisticsSummaryStore = legacyCreateStatisticsSummaryStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqViewStatisticSummariesStore = legacyCreateViewStatisticSummariesStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqPredictionPanelStore = legacyCreatePredictionPanelStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqLayoutStore = legacyCreateLayoutStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqSignalForecastStore = legacyCreateSignalForecastStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqTrendScalingColumnStore = legacyCreateTrendScalingColumnStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqStoredTableStore = legacyCreateStoredTableStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqSummaryReportStore = legacyCreateSummaryReportStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqItemRuntimeStore = legacyCreateItemRuntimeStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqCapsuleGroupStore = legacyCreateCapsuleGroupStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqAggregationBinStore = legacyCreateAggregationBinStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqScalarRuntimeStore = legacyCreateScalarRuntimeStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqCompositeSearchStore = legacyCreateCompositeSearchStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqCapsuleRuntimeStore = legacyCreateCapsuleRuntimeStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqSeriesRuntimeStore = legacyCreateSeriesRuntimeStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqConditionRuntimeStore = legacyCreateConditionRuntimeStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqTableRuntimeStore = legacyCreateTableRuntimeStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqConditionWithPropertiesStore = legacyCreateConditionWithPropertiesStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqEditTemplatePreviewStore = legacyCreateEditTemplatePreviewStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqReferencePanelStore = legacyCreateReferencePanelStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqProfileSearchStore = legacyCreateProfileSearchStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqScatterConditionStore = legacyCreateScatterConditionStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqSignalFromConditionStore = legacyCreateSignalFromConditionStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqSignalSmoothingStore = legacyCreateSignalSmoothingStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqManualConditionStore = legacyCreateManualConditionStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqPeriodicConditionStore = legacyCreatePeriodicConditionStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqValueSearchStore = legacyCreateValueSearchStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/client/packages/webserver/app/src/stores/README.md#migration
 */
export const sqImportDatafileStore = legacyCreateImportDatafileStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/SPEC/webserver/ZUSTAND_MIGRATION_SPEC.md#7-legacy-singleton-bridge
 */
export const sqHomeScreenStore = legacyCreateHomeScreenStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/SPEC/webserver/ZUSTAND_MIGRATION_SPEC.md#7-legacy-singleton-bridge
 */
export const sqExportExcelPanelStore = legacyCreateExportExcelPanelStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/SPEC/webserver/ZUSTAND_MIGRATION_SPEC.md#7-legacy-singleton-bridge
 */
export const sqExportODataPanelStore = legacyCreateExportODataPanelStore();

/**
 * @deprecated Use Zustand store hooks/actions instead.
 * @see https://github.com/seeq12/crab/blob/develop/SPEC/webserver/ZUSTAND_MIGRATION_SPEC.md#7-legacy-singleton-bridge
 */
export const sqItemPropertiesStore = legacyCreateItemPropertiesStore();

export const sqAssetGroupStore = flux.createStore(AssetGroupStore);
export const sqWorkbenchStore = flux.createStore(WorkbenchStore);
export const sqInvestigateStore = flux.createStore(InvestigateStore);
export const sqPluginStore = flux.createStore(PluginStore);
export const sqWorkbookStore = flux.createStore(WorkbookStore);
export const sqDurationStore = flux.createStore(DurationStore);
export const sqWorksheetStore = flux.createStore(WorksheetStore);
export const sqManualSignalStore = flux.createStore(ManualSignalStore);
export const sqTrendMetricStore = flux.createStore(TrendMetricStore);
export const sqTrendTableStore = flux.createStore(TrendTableStore);
export const sqTrendScalarStore = flux.createStore(TrendScalarStore);
export const sqReportStore = flux.createStore(ReportStore);
export const sqTrendSeriesStore = flux.createStore(TrendSeriesStore);
export const sqTrendCapsuleStore = flux.createStore(TrendCapsuleStore);
export const sqTrendConditionStore = flux.createStore(TrendConditionStore);
export const sqTrendStore = flux.createStore(TrendStore);
export const sqTreemapStore = flux.createStore(TreemapStore);
export const sqReportContentStore = flux.createStore(ReportContentStore);
export const sqScatterPlotStore = flux.createStore(ScatterPlotStore);
export const sqTableBuilderStore = flux.createStore(TableBuilderStore);
export const sqFormulaToolStore = flux.createStore(FormulaToolStore);
export const sqScalingTableDefinitionStore = flux.createStore(ScalingTableDefinitionStore);
export const sqItemTableDefinitionStore = flux.createStore(ItemTableDefinitionStore);
export const sqScalingTableEditorStore = flux.createStore(ScalingTableEditorStore);
export const sqItemTableEditorStore = flux.createStore(ItemTableEditorStore);
export const sqItemFinderStore = flux.createStore(ItemFinderStore);
export const sqAssetBuilderStore = flux.createStore(AssetBuilderStore);
export const sqSelfOrganizingMapsStore = flux.createStore(SelfOrganizingMapsStore);
export const sqIsolationForestStore = flux.createStore(IsolationForestStore);
export const sqPartialLeastSquaresStore = flux.createStore(PartialLeastSquaresStore);
export const sqPrincipalComponentAnalysisStore = flux.createStore(PrincipalComponentAnalysisStore);
export const sqVantageContextStore = flux.createStore(VantageContextStore);
export const sqVantageTableStore = flux.createStore(VantageTableStore);
export const sqClusteringStore = flux.createStore(ClusteringStore);

// The runtime stores hold fetched state that belongs to the loaded worksheet and never reaches a
// workstep, so they clear whenever the worksheet stores are force-initialized
sqStateSynchronizer.setInitializeCallback((persistenceLevel) => {
  if (persistenceLevel === 'WORKSHEET') {
    [
      sqItemRuntimeStore,
      sqSeriesRuntimeStore,
      sqConditionRuntimeStore,
      sqScalarRuntimeStore,
      sqTableRuntimeStore,
      sqCapsuleRuntimeStore,
    ].forEach((store) => store.actions.reset());
  }
});
